// navigation-completion Stage E — bookmark hierarchy tree + edit controls.
//
// Every row uses the SERVER-supplied handle (never an index derived locally);
// move/indent/outdent/remove/rename/retarget each produce ONE NavEdit the parent
// applies under compare-and-swap. The parent's policy decisions gate which
// controls are live; a denied control is disabled WITH its server reason rather
// than hidden.

import type { RemoteNavigationPreviewCallback } from './useRemoteNavigationPreview';
import { useEffect, useState, type ReactElement } from 'react';
import type {
  InventoryBookmark,
  NavEdit,
  OperationDecisions,
} from '@/api/documentNavigation';
import {
  authReasonText,
  describeDestination,
  draftFromTarget,
  navStyles,
  targetFromDraft,
  EMPTY_LOCAL_TARGET,
  EMPTY_REMOTE_TARGET,
  type TargetDraft,
} from './navShared';
import { TargetFields } from './navTargetFields';
import type { PdfPreviewFocus } from './PdfCanvasViewer';

let newHandleCounter = 0;
function freshHandle(): string {
  newHandleCounter += 1;
  return `new-bm-${newHandleCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

interface SiblingPos {
  readonly index: number;
  readonly count: number;
}

/** Reconstruct sibling position for each pre-order bookmark from depth alone so
 *  move-up/down can send a sibling index the engine accepts (0-based among the
 *  node's own parent's children). The parent of a node is the nearest preceding
 *  node at depth-1 (tracked via a depth-indexed stack of handles). */
function siblingPositions(bookmarks: readonly InventoryBookmark[]): Map<string, SiblingPos> {
  const parentKeys: string[] = [];
  const stack: string[] = []; // handle at each depth level
  for (const bm of bookmarks) {
    const parentKey = bm.depth <= 1 ? '(root)' : (stack[bm.depth - 1] ?? '(root)');
    parentKeys.push(parentKey);
    stack[bm.depth] = bm.handle;
    stack.length = bm.depth + 1;
  }
  const counts = new Map<string, number>();
  for (const pk of parentKeys) counts.set(pk, (counts.get(pk) ?? 0) + 1);
  const running = new Map<string, number>();
  const out = new Map<string, SiblingPos>();
  bookmarks.forEach((bm, i) => {
    const pk = parentKeys[i] ?? '(root)';
    const idx = running.get(pk) ?? 0;
    running.set(pk, idx + 1);
    out.set(bm.handle, { index: idx, count: counts.get(pk) ?? idx + 1 });
  });
  return out;
}

export interface BookmarkEditorProps {
  readonly bookmarks: readonly InventoryBookmark[];
  readonly outlineIncomplete: boolean;
  readonly pageCount: number | null;
  readonly canMutate: boolean;
  readonly mutationBlockedReason: string | null;
  readonly decisions: OperationDecisions;
  readonly busy: boolean;
  readonly onEdit: (edit: NavEdit, actionKey: string) => void;
  readonly onJumpToPage: (page1: number) => void;
  /** Rich preview request (destination page / named destination, tone). When
   *  present the "View destination" affordance routes through it; the plain
   *  {@link onJumpToPage} remains the fallback for a build without a viewer. */
  readonly onPreviewFocus?: (focus: PdfPreviewFocus) => void;
  /** Preview a cross-document target's exact bytes in the workspace viewer. */
  readonly onPreview?: RemoteNavigationPreviewCallback;
  /** The source document id — enables the cross-document target picker. */
  readonly documentId: string;
  /** Reports whether a row rename/retarget or the add form is open with unsaved
   *  input, so the parent can register it as workspace draft state. */
  readonly onDirtyChange?: (dirty: boolean) => void;
}

export function BookmarkEditor({
  bookmarks,
  outlineIncomplete,
  pageCount,
  canMutate,
  mutationBlockedReason,
  decisions,
  busy,
  onEdit,
  onJumpToPage,
  onPreviewFocus,
  onPreview,
  documentId,
  onDirtyChange,
}: BookmarkEditorProps): ReactElement {
  const [editing, setEditing] = useState<{ handle: string; mode: 'rename' | 'retarget' } | null>(null);
  const [renameText, setRenameText] = useState('');
  const [retargetDraft, setRetargetDraft] = useState<TargetDraft>(EMPTY_LOCAL_TARGET);
  const [adding, setAdding] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addDraft, setAddDraft] = useState<TargetDraft>(EMPTY_LOCAL_TARGET);
  const [localError, setLocalError] = useState<string | null>(null);
  useEffect(() => { onDirtyChange?.(editing !== null || adding); }, [editing, adding, onDirtyChange]);

  const createDec = decisions.createBookmark;
  const modifyDec = decisions.modifyBookmark;
  const canCreate = canMutate && !!createDec?.allowed;
  const canModify = canMutate && !!modifyDec?.allowed;
  const createReason = !canMutate
    ? mutationBlockedReason
    : createDec && !createDec.allowed
      ? authReasonText(createDec.reason)
      : null;
  const modifyReason = !canMutate
    ? mutationBlockedReason
    : modifyDec && !modifyDec.allowed
      ? authReasonText(modifyDec.reason)
      : null;

  const positions = siblingPositions(bookmarks);

  function submitRename(handle: string): void {
    const title = renameText.trim();
    if (title.length === 0) {
      setLocalError('a bookmark title is required');
      return;
    }
    setLocalError(null);
    setEditing(null);
    onEdit({ op: 'renameBookmark', handle, title }, `bm-rename-${handle}`);
  }

  function submitRetarget(handle: string): void {
    const r = targetFromDraft(retargetDraft, pageCount);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setLocalError(null);
    setEditing(null);
    onEdit({ op: 'retargetBookmark', handle, target: r.target }, `bm-retarget-${handle}`);
  }

  function submitAdd(): void {
    const title = addTitle.trim();
    if (title.length === 0) {
      setLocalError('a bookmark title is required');
      return;
    }
    const r = targetFromDraft(addDraft, pageCount);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setLocalError(null);
    setAdding(false);
    setAddTitle('');
    setAddDraft(EMPTY_LOCAL_TARGET);
    onEdit(
      { op: 'createBookmark', handle: freshHandle(), parentHandle: null, index: 'append', title, target: r.target },
      'bm-create',
    );
  }

  return (
    <section style={navStyles.editorPanel} data-testid="nav-bookmarks" aria-label="Bookmarks">
      {outlineIncomplete && (
        <div style={navStyles.note} data-testid="nav-bookmarks-incomplete">
          The outline scan did not finish — the list below may be incomplete, and structural edits
          to it are unsafe. Resolve the source before editing bookmarks.
        </div>
      )}
      {bookmarks.length === 0 ? (
        <div style={navStyles.muted} data-testid="nav-bookmarks-empty">
          No bookmarks found in this artifact.
        </div>
      ) : (
        <div data-testid="nav-bookmark-tree">
          {bookmarks.map((bm) => {
            const pos = positions.get(bm.handle);
            const isFirst = pos ? pos.index === 0 : true;
            const isLast = pos ? pos.index >= pos.count - 1 : true;
            const isTopLevel = bm.depth <= 1;
            const rowEditing = editing?.handle === bm.handle;
            // A disabled structural control explains the STRUCTURAL constraint
            // (no-op reorder, no parent to outdent to) when the operation is
            // otherwise permitted, falling back to the policy reason when it is
            // not. A control is never disabled without a reason the reviewer can
            // read.
            const structural = (blocked: string | null): string | null =>
              !canModify ? modifyReason : blocked;
            // The bookmark's destination as a preview focus (destination tone),
            // or null for remote/external/other/none (no in-document page).
            const destFocus: PdfPreviewFocus | null =
              bm.destination.kind === 'localPage'
                ? { page: bm.destination.pageIndex + 1, tone: 'destination', label: bm.title || `Page ${bm.destination.pageIndex + 1}` }
                : bm.destination.kind === 'named'
                  ? { named: bm.destination.name, tone: 'destination', label: bm.title || `“${bm.destination.name}”` }
                  : null;
            return (
              <div key={bm.handle} style={{ ...navStyles.row, marginLeft: Math.max(0, bm.depth - 1) * 16 }} data-testid={`nav-bm-${bm.handle}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={navStyles.label}>{bm.title || '(untitled bookmark)'}</span>
                  <span style={navStyles.muted}>
                    {describeDestination(bm.destination)}
                    {!bm.destinationResolves && bm.destination.kind !== 'remote' && bm.destination.kind !== 'external' && (
                      <span style={{ color: 'var(--danger, #b91c1c)' }} data-testid={`nav-bm-unresolved-${bm.handle}`}>
                        {' '}· destination does not resolve
                      </span>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                  {/* Only offer the affordance when it can actually DO something: a
                      rich viewer resolves any destination (page or named), while
                      the plain jump fallback can only reach an in-document PAGE.
                      Without a viewer, a named destination has no page to jump to,
                      so the button is omitted rather than rendered as a no-op. */}
                  {destFocus && (onPreviewFocus !== undefined || destFocus.page !== undefined) && (
                    <button
                      type="button"
                      style={navStyles.secondaryBtn}
                      data-testid={`nav-bm-jump-${bm.handle}`}
                      onClick={() =>
                        onPreviewFocus
                          ? onPreviewFocus(destFocus)
                          : destFocus.page !== undefined
                            ? onJumpToPage(destFocus.page)
                            : undefined
                      }
                    >
                      {onPreviewFocus ? 'View destination' : 'Jump to page'}
                    </button>
                  )}
                  <RowBtn
                    label="Rename"
                    disabled={!canModify || busy}
                    reason={modifyReason}
                    testid={`nav-bm-rename-btn-${bm.handle}`}
                    onClick={() => {
                      setRenameText(bm.title);
                      setEditing({ handle: bm.handle, mode: 'rename' });
                      setLocalError(null);
                    }}
                  />
                  <RowBtn
                    label="Change target"
                    disabled={!canModify || busy}
                    reason={modifyReason}
                    testid={`nav-bm-retarget-btn-${bm.handle}`}
                    onClick={() => {
                      setRetargetDraft(
                        bm.destination.kind === 'localPage'
                          ? draftFromTarget({ kind: 'localPage', pageIndex: bm.destination.pageIndex })
                          : bm.destination.kind === 'named'
                            ? draftFromTarget({ kind: 'named', name: bm.destination.name })
                            : bm.destination.kind === 'remote'
                              ? EMPTY_REMOTE_TARGET
                              : EMPTY_LOCAL_TARGET,
                      );
                      setEditing({ handle: bm.handle, mode: 'retarget' });
                      setLocalError(null);
                    }}
                  />
                  <RowBtn
                    label="Move up"
                    disabled={!canModify || busy || isFirst}
                    reason={structural(isFirst ? 'already first among its siblings' : null)}
                    testid={`nav-bm-up-${bm.handle}`}
                    onClick={() => pos && onEdit({ op: 'reorderBookmark', handle: bm.handle, index: pos.index - 1 }, `bm-up-${bm.handle}`)}
                  />
                  <RowBtn
                    label="Move down"
                    disabled={!canModify || busy || isLast}
                    reason={structural(isLast ? 'already last among its siblings' : null)}
                    testid={`nav-bm-down-${bm.handle}`}
                    onClick={() => pos && onEdit({ op: 'reorderBookmark', handle: bm.handle, index: pos.index + 1 }, `bm-down-${bm.handle}`)}
                  />
                  <RowBtn
                    label="Indent"
                    disabled={!canModify || busy || isFirst}
                    reason={structural(isFirst ? 'the first item has no preceding sibling to nest under' : null)}
                    testid={`nav-bm-indent-${bm.handle}`}
                    onClick={() => onEdit({ op: 'indentBookmark', handle: bm.handle }, `bm-indent-${bm.handle}`)}
                  />
                  <RowBtn
                    label="Outdent"
                    disabled={!canModify || busy || isTopLevel}
                    reason={structural(isTopLevel ? 'a top-level bookmark has no parent to outdent to' : null)}
                    testid={`nav-bm-outdent-${bm.handle}`}
                    onClick={() => onEdit({ op: 'outdentBookmark', handle: bm.handle }, `bm-outdent-${bm.handle}`)}
                  />
                  <RowBtn
                    label="Remove"
                    tone="danger"
                    disabled={!canModify || busy}
                    reason={modifyReason}
                    testid={`nav-bm-remove-${bm.handle}`}
                    onClick={() => onEdit({ op: 'removeBookmark', handle: bm.handle }, `bm-remove-${bm.handle}`)}
                  />
                </div>
                {rowEditing && editing?.mode === 'rename' && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input
                      style={{ ...navStyles.input, width: 220 }}
                      data-testid={`nav-bm-rename-input-${bm.handle}`}
                      aria-label="New bookmark title"
                      value={renameText}
                      onChange={(e) => setRenameText(e.target.value)}
                    />
                    <button type="button" style={busy ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} data-testid={`nav-bm-rename-save-${bm.handle}`} disabled={busy} onClick={() => submitRename(bm.handle)}>
                      Save
                    </button>
                    <button type="button" style={navStyles.secondaryBtn} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                )}
                {rowEditing && editing?.mode === 'retarget' && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TargetFields value={retargetDraft} onChange={setRetargetDraft} pageCount={pageCount} idPrefix={`nav-bm-retarget-${bm.handle}`} documentId={documentId} {...(onPreview ? { onPreview } : {})} {...(onPreviewFocus ? { onPreviewFocus } : {})} />
                    <button type="button" style={busy || !canModify || (retargetDraft.kind === 'remote' && !retargetDraft.selection) ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} data-testid={`nav-bm-retarget-save-${bm.handle}`} disabled={busy || !canModify || (retargetDraft.kind === 'remote' && !retargetDraft.selection)} onClick={() => submitRetarget(bm.handle)}>
                      Save
                    </button>
                    <button type="button" style={navStyles.secondaryBtn} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        {adding ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }} data-testid="nav-bm-add-form">
            <input
              style={{ ...navStyles.input, width: 200 }}
              placeholder="Bookmark title"
              aria-label="New bookmark title"
              data-testid="nav-bm-add-title"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
            />
            <TargetFields value={addDraft} onChange={setAddDraft} pageCount={pageCount} idPrefix="nav-bm-add" documentId={documentId} {...(onPreview ? { onPreview } : {})} {...(onPreviewFocus ? { onPreviewFocus } : {})} />
            <button type="button" style={busy || !canCreate || (addDraft.kind === 'remote' && !addDraft.selection) ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} data-testid="nav-bm-add-save" disabled={busy || !canCreate || (addDraft.kind === 'remote' && !addDraft.selection)} onClick={submitAdd}>
              Add bookmark
            </button>
            <button type="button" style={navStyles.secondaryBtn} onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <RowBtn
            label="Add bookmark"
            disabled={!canCreate || busy}
            reason={createReason}
            testid="nav-bm-add"
            onClick={() => {
              setAdding(true);
              setAddTitle('');
              setAddDraft(EMPTY_LOCAL_TARGET);
              setLocalError(null);
            }}
          />
        )}
      </div>
      {localError && (
        <div style={{ ...navStyles.error, marginTop: 6 }} role="alert" data-testid="nav-bm-error">
          {localError}
        </div>
      )}
    </section>
  );
}

interface RowBtnProps {
  readonly label: string;
  readonly disabled: boolean;
  readonly reason: string | null;
  readonly testid: string;
  readonly onClick: () => void;
  /** 'danger' marks a destructive action (Remove) so it reads apart from the
   *  neutral secondary controls; defaults to 'secondary'. */
  readonly tone?: 'secondary' | 'danger';
}

/** A readable action button that, when disabled, carries the server/policy
 *  reason as its title so a disabled control is never unexplained. */
function RowBtn({ label, disabled, reason, testid, onClick, tone = 'secondary' }: RowBtnProps): ReactElement {
  const enabledStyle = tone === 'danger' ? navStyles.dangerBtn : navStyles.secondaryBtn;
  const disabledStyle = tone === 'danger' ? navStyles.dangerBtnDisabled : navStyles.secondaryBtnDisabled;
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      aria-disabled={disabled}
      title={disabled && reason ? reason : undefined}
      style={disabled ? disabledStyle : enabledStyle}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
